import {
  AlertCircle,
  CalendarDays,
  ChevronDown,
  CircleCheck,
  Crown,
  History,
  Info,
  Loader2,
  Plus,
  RotateCcw,
  ShieldCheck,
  X,
} from "lucide-react";
import posthog from "posthog-js";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useStripe } from "../contexts/StripeContext";
import { useEscapeClosesPopoverInDialog } from "../hooks/useEscapeClosesPopoverInDialog";
import { useUpgradePage } from "../hooks/useUpgradePage";
import { FIRST_COMMENT_PLATFORMS } from "../lib/publishDefaults";
import { cn } from "../lib/utils";
import { PlatformConnection, PlatformType } from "../models/Platform";
import {
  FacebookSettings,
  FirstCommentOverrides,
  MediaItem,
  PlatformSpecificSettings,
} from "../models/Post";
import { ScheduleByDate } from "../utils/scheduleDensity";
import { PlatformConnectionIcon } from "./PlatformConnectionIcon";
import {
  channelPublishesVideo,
  getConnectionErrors,
  getPlatformConfig,
  PlatformConnectionSettings,
} from "./PlatformSettings";
import { SchedulePicker } from "./SchedulePicker";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";
import { Label } from "./ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Switch } from "./ui/switch";
import { Textarea } from "./ui/textarea";

type ConnectionSettings = PlatformSpecificSettings[keyof PlatformSpecificSettings];

const INHERITED_FIELD =
  "border-transparent bg-muted/70 text-muted-foreground hover:border-input focus-visible:bg-background focus-visible:text-foreground";

export type ConnectionsLoadStatus = "loading" | "loaded" | "error";

interface PublishModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connections: PlatformConnection[];
  /** Until "loaded", no channels means "not known yet", not "none connected". */
  connectionsStatus?: ConnectionsLoadStatus;
  onRetryConnections?: () => void;
  selectedConnectionIds: Set<string>;
  onToggleConnection: (connectionId: string, selected: boolean) => void;
  /** Why a channel can't be ticked (already published, expired, ...), or null. */
  getDisabledReason: (connection: PlatformConnection) => string | null;
  /** Channels this post already went out on, with when (0 if unknown). */
  publishedAtById?: Record<string, number>;
  /** Opens the modal on a channel; the nonce re-triggers for the same one. */
  focusRequest?: { connectionId: string; nonce: number } | null;
  platformSettings: Record<string, ConnectionSettings>;
  onSettingsChange: (connectionId: string, settings: Partial<ConnectionSettings>) => void;
  mediaItems: MediaItem[];
  isVideoPost: boolean;
  postTitle: string;
  postDescription: string;
  /** The post's first comment text (sidebar), or the article-link default. */
  postFirstComment: string;
  defaultPinterestLink?: string;
  pinterestDescriptionGeneratingIds?: ReadonlySet<string>;
  /** The platform preview for one channel, rendered by the editor. */
  renderPreview: (connection: PlatformConnection) => ReactNode;
  scheduleByDate: ScheduleByDate;
  connectionsById: Record<string, PlatformConnection>;
  /** Times already set on this post (rescheduling), keyed by connection. */
  existingSchedules: Record<string, number>;
  /** A time picked before the editor (best-time links): prefill it for every channel. */
  preferredTime?: number;
  timezone: string;
  timezoneLabel: string;
  /** Something that blocks publishing everywhere (uploads, no media, plan limit). */
  blockingMessage?: string | null;
  /** How to resolve the block, shown beside the message. */
  blockingAction?: { label: string; onClick: () => void };
  isPublishing: boolean;
  onPublishNow: () => void;
  onSchedule: (times: Record<string, number>) => void;
  /** Open with the schedule panel showing (e.g. a "schedule" link intent). */
  startScheduling?: boolean;
  /** Connect another account in place (saves the post, then OAuth). */
  onConnectPlatform: (platform: PlatformType) => void;
  /** No room for another account on this plan. */
  atConnectionLimit: boolean;
  maxConnections: number;
  onUpgradeConnections: () => void;
}

/** Platforms people can connect from the editor, in Settings' order. */
const CONNECTABLE_PLATFORMS: PlatformType[] = [
  "linkedin",
  "instagram",
  "facebook",
  "pinterest",
  "tiktok",
  "youtube",
];

export function PublishModal({
  open,
  onOpenChange,
  connections,
  connectionsStatus = "loaded",
  onRetryConnections,
  selectedConnectionIds,
  onToggleConnection,
  getDisabledReason,
  publishedAtById = {},
  focusRequest,
  platformSettings,
  onSettingsChange,
  mediaItems,
  isVideoPost,
  postTitle,
  postDescription,
  postFirstComment,
  defaultPinterestLink,
  pinterestDescriptionGeneratingIds,
  renderPreview,
  scheduleByDate,
  connectionsById,
  existingSchedules,
  preferredTime,
  timezone,
  timezoneLabel,
  blockingMessage,
  blockingAction,
  isPublishing,
  onPublishNow,
  onSchedule,
  startScheduling = false,
  onConnectPlatform,
  atConnectionLimit,
  maxConnections,
  onUpgradeConnections,
}: PublishModalProps) {
  const { currentUser } = useAuth();
  const channels = useMemo(
    () => connections.filter((connection) => !!getPlatformConfig(connection.platform)),
    [connections]
  );
  const selected = channels.filter((connection) => selectedConnectionIds.has(connection.id));
  // Reopened on a post that already went out everywhere (an old link, a
  // restored tab): say so instead of asking for a channel nobody can tick.
  const isPublishedEverywhere =
    channels.length > 0 && channels.every((connection) => connection.id in publishedAtById);
  const publishedLabel = (connection: PlatformConnection) => {
    const publishedAt = publishedAtById[connection.id];
    return publishedAt ? `Published ${formatDay(publishedAt)}` : "Published";
  };

  const [focusId, setFocusId] = useState<string | null>(null);
  const focus =
    channels.find((connection) => connection.id === focusId) ?? selected[0] ?? channels[0];

  // Open on the first ticked channel, or wherever the caller asked.
  useEffect(() => {
    if (!open) return;
    setFocusId(focusRequest?.connectionId ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, focusRequest?.nonce]);

  // Facebook Page stories only take photos. A channel publishing video can't
  // stay on Story, whether Story was remembered from its last post or the
  // format just switched to Video, so move it to the feed.
  useEffect(() => {
    if (!open) return;
    for (const connection of selected) {
      if (connection.platform !== "facebook") continue;
      const settings = platformSettings[connection.id] as FacebookSettings | undefined;
      if (
        settings?.postType === "story" &&
        channelPublishesVideo("facebook", settings, mediaItems, isVideoPost)
      ) {
        onSettingsChange(connection.id, { postType: "feed" });
      }
    }
  }, [open, selected, platformSettings, mediaItems, isVideoPost, onSettingsChange]);

  const errorsById = useMemo(() => {
    const result: Record<string, string[]> = {};
    for (const connection of selected) {
      const errors = getConnectionErrors({
        connection,
        settings: platformSettings[connection.id],
        mediaItems,
        isVideoPost,
        postTitle,
        postDescription,
      });
      if (errors.length) result[connection.id] = errors;
    }
    return result;
  }, [selected, platformSettings, mediaItems, isVideoPost, postTitle, postDescription]);
  const firstErrorChannel = selected.find((connection) => errorsById[connection.id]);

  // ---- Schedule tray: every ticked channel, prefilled with its next open slot
  const [isScheduling, setIsScheduling] = useState(false);
  const [times, setTimes] = useState<Record<string, number>>({});
  const [suggested, setSuggested] = useState<Record<string, number>>({});
  const [isSuggesting, setIsSuggesting] = useState(false);
  const isReschedule = Object.keys(existingSchedules).length > 0;

  useEffect(() => {
    if (!open) return;
    setTimes(existingSchedules);
    setSuggested({});
    setIsScheduling(isReschedule || startScheduling);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !isScheduling || !currentUser) return;
    const missing = selected.filter((connection) => !times[connection.id]);
    if (missing.length === 0) return;
    if (preferredTime && preferredTime > Date.now()) {
      const chosen = Object.fromEntries(missing.map((connection) => [connection.id, preferredTime]));
      setTimes((prev) => ({ ...chosen, ...prev }));
      return;
    }
    let cancelled = false;
    setIsSuggesting(true);
    (async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE}/schedule/suggest`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${await currentUser.getIdToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            platforms: [...new Set(missing.map((connection) => connection.platform))],
            timezone,
            connectionIds: missing.map((connection) => connection.id),
          }),
        });
        const data = response.ok ? await response.json() : { suggestions: [] };
        if (cancelled) return;
        const suggestions = (data.suggestions || []) as {
          platform: PlatformType;
          connectionId?: string;
          scheduledFor: number;
        }[];
        const next: Record<string, number> = {};
        for (const connection of missing) {
          const suggestion =
            suggestions.find((s) => s.connectionId === connection.id) ??
            suggestions.find((s) => s.platform === connection.platform);
          next[connection.id] = suggestion?.scheduledFor ?? nextHour();
        }
        setSuggested((prev) => ({ ...prev, ...next }));
        setTimes((prev) => ({ ...next, ...prev }));
      } catch {
        if (cancelled) return;
        const fallback = Object.fromEntries(missing.map((connection) => [connection.id, nextHour()]));
        setTimes((prev) => ({ ...fallback, ...prev }));
      } finally {
        if (!cancelled) setIsSuggesting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isScheduling, selected.map((connection) => connection.id).join(",")]);

  const blockOrFocusError = () => {
    if (firstErrorChannel) {
      setFocusId(firstErrorChannel.id);
      return true;
    }
    return !!blockingMessage || selected.length === 0;
  };

  const handlePublishNow = () => {
    if (blockOrFocusError()) return;
    onPublishNow();
  };
  const handleScheduleAll = () => {
    if (blockOrFocusError()) return;
    const chosen = Object.fromEntries(
      selected.map((connection) => [connection.id, times[connection.id]]).filter(([, time]) => !!time)
    );
    if (Object.keys(chosen).length !== selected.length) return;
    onSchedule(chosen);
  };

  // Connecting redirects away; flag it first so the spinner paints.
  const [connectingPlatform, setConnectingPlatform] = useState<PlatformType | null>(null);
  useEffect(() => {
    if (!connectingPlatform) return;
    if (process.env.REACT_APP_POSTHOG_DISABLED !== "true") {
      posthog.capture("connect_started", {
        platform: connectingPlatform,
        surface: "publish_modal",
      });
    }
    onConnectPlatform(connectingPlatform);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectingPlatform]);
  const connectButtons = (variant: "large" | "menu") =>
    CONNECTABLE_PLATFORMS.map((platform) => {
      const config = getPlatformConfig(platform);
      const Icon = config.icon;
      const isConnecting = connectingPlatform === platform;
      return (
        <button
          key={platform}
          type="button"
          onClick={() => setConnectingPlatform(platform)}
          disabled={connectingPlatform !== null}
          title={
            platform === "instagram"
              ? "Connect Instagram (needs a Business or Creator account)"
              : `Connect ${config.name}`
          }
          className={cn(
            "flex items-center gap-2.5 rounded-lg border text-sm font-medium transition-colors hover:bg-muted disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent",
            variant === "large" ? "h-11 justify-center px-4" : "h-9 w-full px-3"
          )}
        >
          {isConnecting ? (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          ) : (
            <Icon className={cn("h-4 w-4", config.color)} />
          )}
          {config.name}
        </button>
      );
    });

  // Keep the focused channel fully in view in the top row.
  const railRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    railRef.current
      ?.querySelector<HTMLElement>(`[data-channel="${focus?.id}"]`)
      ?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
  }, [focus?.id]);

  const [isSwitcherOpen, setIsSwitcherOpen] = useState(false);
  const [isConnectMenuOpen, setIsConnectMenuOpen] = useState(false);
  // These menus render inside the dialog (portal={false}) and aren't modal,
  // so they never take over <body>'s pointer-events; Escape still needs this
  // to close just the menu (see the hook for why).
  const closeSwitcher = useCallback(() => setIsSwitcherOpen(false), []);
  const closeConnectMenu = useCallback(() => setIsConnectMenuOpen(false), []);
  useEscapeClosesPopoverInDialog(isSwitcherOpen, closeSwitcher);
  useEscapeClosesPopoverInDialog(isConnectMenuOpen, closeConnectMenu);

  // Leaving for another page (upgrade, settings) from inside the modal. The
  // dialog and its menus lock <body> (pointer-events: none) while open; a
  // route change that unmounts them mid-flight can leave that lock behind,
  // so the next page doesn't respond. Close first, navigate once they're
  // gone, and mark the URL so Back lands in this modal again.
  const leaveTo = (go: () => void) => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("intent", "publish-now");
      window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
    } catch {
      // URL APIs unavailable — Back just returns to the editor.
    }
    setIsSwitcherOpen(false);
    setIsConnectMenuOpen(false);
    onOpenChange(false);
    requestAnimationFrame(() => requestAnimationFrame(go));
  };
  // Belt and braces: never leave the body locked behind us.
  useEffect(
    () => () => {
      document.body.style.pointerEvents = "";
    },
    []
  );
  const channelChip = (connection: PlatformConnection, variant: "rail" | "list") => {
    const isOn = selectedConnectionIds.has(connection.id);
    const disabledReason = getDisabledReason(connection);
    const error = isOn ? errorsById[connection.id]?.[0] : undefined;
    const draftTime = isScheduling && isOn ? times[connection.id] : undefined;
    const label = connection.id in publishedAtById
      ? publishedLabel(connection)
      : disabledReason
        ? disabledReason
        : error
          ? "Needs attention"
          : !isOn
            ? "Not publishing"
            : draftTime
              ? formatShort(draftTime)
              : getPlatformConfig(connection.platform)?.name;
    const isFocused = focus?.id === connection.id;
    return (
      <div
        key={connection.id}
        data-channel={variant === "rail" ? connection.id : undefined}
        className={cn(
          "flex shrink-0 items-center gap-2 rounded-lg border border-transparent pl-1.5 pr-2.5 transition-colors",
          variant === "rail" ? "h-11" : "h-12 w-full",
          isFocused
            ? variant === "rail"
              ? "border-foreground bg-background shadow-sm"
              : "bg-muted"
            : "hover:bg-muted"
        )}
      >
        <button
          type="button"
          onClick={() => {
            setFocusId(connection.id);
            setIsSwitcherOpen(false);
          }}
          className="flex h-full min-w-0 flex-1 items-center gap-2 text-left"
        >
          <span className={cn("relative shrink-0", !isOn && "opacity-45")}>
            <PlatformConnectionIcon
              platform={connection.platform}
              profileImage={connection.profileImage}
              profileName={connection.profileName}
              size="md"
              disableTooltip
            />
            {error && (
              <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-destructive ring-2 ring-background" />
            )}
          </span>
          <span className={cn("flex min-w-0 flex-col leading-tight", !isOn && "opacity-45")}>
            <span
              className={cn(
                "truncate text-[13px] font-medium",
                variant === "rail" && "max-w-[9rem]",
                !isOn && "line-through"
              )}
            >
              {connection.profileName}
            </span>
            <span
              className={cn(
                "whitespace-nowrap text-[11.5px] text-muted-foreground",
                error && "text-destructive",
                draftTime && !error && "tabular-nums text-foreground"
              )}
            >
              {label}
            </span>
          </span>
        </button>
        <Checkbox
          checked={isOn}
          disabled={!!disabledReason}
          onCheckedChange={(checked) => onToggleConnection(connection.id, checked === true)}
          aria-label={`Publish to ${getPlatformConfig(connection.platform)?.name} ${connection.profileName}`}
          className="ml-1 shrink-0"
        />
      </div>
    );
  };

  const count = selected.length;
  const showScheduleTray = isScheduling && channels.length > 0;
  // No channels yet because they haven't loaded is not "none connected".
  const channelsUnknown = channels.length === 0 && connectionsStatus !== "loaded";
  const status = channelsUnknown ? (
    <span className="text-sm text-muted-foreground">
      {connectionsStatus === "error" ? "Couldn't load your channels" : "Loading your channels…"}
    </span>
  ) : channels.length === 0 ? (
    <span className="text-sm text-muted-foreground">Connect a channel to publish</span>
  ) : isPublishedEverywhere ? (
    <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
      <CircleCheck className="h-4 w-4" />
      {channels.length === 1
        ? `Already published to ${channels[0].profileName}`
        : "Already published to all your channels"}
    </span>
  ) : blockingMessage ? (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
      <span className="flex items-center gap-1.5 font-medium text-destructive">
        <AlertCircle className="h-4 w-4 shrink-0" />
        {blockingMessage}
      </span>
      {blockingAction && (
        <button
          type="button"
          onClick={() => leaveTo(blockingAction.onClick)}
          className="font-medium text-foreground underline decoration-border underline-offset-2 hover:decoration-foreground"
        >
          {blockingAction.label}
        </button>
      )}
    </span>
  ) : firstErrorChannel ? (
    <button
      type="button"
      onClick={() => setFocusId(firstErrorChannel.id)}
      className="-ml-2 flex items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-sm font-medium text-destructive hover:bg-destructive/10"
    >
      <AlertCircle className="h-4 w-4 shrink-0" />
      {Object.keys(errorsById).length > 1
        ? `${Object.keys(errorsById).length} channels need attention`
        : `${getPlatformConfig(firstErrorChannel.platform)?.name}: ${errorsById[firstErrorChannel.id][0]}`}
    </button>
  ) : (
    <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
      {count > 0 && <CircleCheck className="h-4 w-4" />}
      {count === 0 ? "Select at least one channel" : `Ready for ${count} channel${count === 1 ? "" : "s"}`}
    </span>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "flex h-[100dvh] max-h-[100dvh] w-full max-w-none flex-col gap-0 overflow-hidden p-0",
          "sm:h-[min(860px,calc(100dvh-48px))] sm:w-[min(1280px,calc(100vw-48px))] sm:max-w-none sm:rounded-xl",
          "[&>button:last-child]:hidden"
        )}
      >
        {/* Header: every connected channel, ticked by default */}
        <div className="flex min-h-16 shrink-0 items-center gap-3 border-b py-2.5 pl-4 pr-2 md:gap-4 md:pl-5 md:pr-3">
          <div className="shrink-0">
            <DialogTitle className="text-lg font-semibold tracking-tight">Publish</DialogTitle>
            <DialogDescription className="text-xs">
              {channelsUnknown
                ? connectionsStatus === "error"
                  ? "Channels didn't load"
                  : "Loading channels…"
                : channels.length === 0
                ? "No channels connected"
                : count === channels.length
                  ? `All ${count} channels`
                  : `${count} of ${channels.length} channels`}
            </DialogDescription>
          </div>
          <div className="hidden h-7 w-px shrink-0 bg-border md:block" />
          {/* Desktop: every channel in a row */}
          <div ref={railRef} className="hidden min-w-0 flex-1 gap-1.5 overflow-x-auto p-0.5 [scrollbar-width:none] md:flex">
            {channels.map((connection) => channelChip(connection, "rail"))}
          </div>
          {/* Phone: the current channel, switching from a list */}
          {channels.length > 0 && focus && (
            <Popover open={isSwitcherOpen} onOpenChange={setIsSwitcherOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border px-2 py-1.5 text-left md:hidden"
                  aria-label="Switch channel"
                >
                  <span className="relative shrink-0">
                    <PlatformConnectionIcon
                      platform={focus.platform}
                      profileImage={focus.profileImage}
                      profileName={focus.profileName}
                      size="sm"
                      disableTooltip
                    />
                    {Object.keys(errorsById).length > 0 && (
                      <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-destructive ring-2 ring-background" />
                    )}
                  </span>
                  <span className="flex min-w-0 flex-col leading-tight">
                    <span className="truncate text-[13px] font-medium">{focus.profileName}</span>
                    <span className="truncate text-[11.5px] text-muted-foreground">
                      {getPlatformConfig(focus.platform)?.name}
                    </span>
                  </span>
                  <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              </PopoverTrigger>
              <PopoverContent portal={false} align="start" className="w-[calc(100vw-2rem)] max-w-sm p-1.5">
                <div className="max-h-[60dvh] space-y-0.5 overflow-y-auto">
                  {channels.map((connection) => channelChip(connection, "list"))}
                </div>
                <div className="mt-1 border-t pt-1">
                  {atConnectionLimit ? (
                    <button
                      type="button"
                      onClick={() => leaveTo(onUpgradeConnections)}
                      className="flex h-10 w-full items-center gap-2 rounded-md px-2.5 text-sm text-muted-foreground hover:bg-muted"
                    >
                      <Plus className="h-4 w-4" />
                      Upgrade to connect another channel
                    </button>
                  ) : (
                    <details className="group">
                      <summary className="flex h-10 cursor-pointer list-none items-center gap-2 rounded-md px-2.5 text-sm font-medium hover:bg-muted">
                        <Plus className="h-4 w-4" />
                        Connect another channel
                      </summary>
                      <div className="space-y-1 px-1 pb-1 pt-0.5">{connectButtons("menu")}</div>
                    </details>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          )}
          {channels.length > 0 && (
            <Popover open={isConnectMenuOpen} onOpenChange={setIsConnectMenuOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="hidden h-8 w-8 shrink-0 text-muted-foreground md:inline-flex"
                  aria-label="Connect another channel"
                  title="Connect another channel"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent portal={false} align="end" className="w-64 p-2">
                {atConnectionLimit ? (
                  <div className="space-y-2 p-1.5">
                    <p className="text-sm font-medium">Connect another channel</p>
                    <p className="text-xs text-muted-foreground">
                      Your plan includes {maxConnections} channel{maxConnections === 1 ? "" : "s"}. Upgrade to connect more.
                    </p>
                    <Button size="sm" className="w-full bg-[#FF5C35] text-white hover:bg-[#E5502F]" onClick={() => leaveTo(onUpgradeConnections)}>
                      Upgrade
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <p className="px-1.5 pb-1 pt-0.5 text-xs font-medium text-muted-foreground">Connect another channel</p>
                    {connectButtons("menu")}
                  </div>
                )}
              </PopoverContent>
            </Popover>
          )}
          <div className="hidden h-7 w-px shrink-0 bg-border md:block" />
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-muted-foreground" onClick={() => onOpenChange(false)} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {channelsUnknown && (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 bg-muted/40 p-8 text-center">
            {connectionsStatus === "error" ? (
              <>
                <div className="space-y-1.5">
                  <h3 className="text-lg font-semibold tracking-tight">Couldn't load your channels</h3>
                  <p className="mx-auto max-w-sm text-sm text-muted-foreground">
                    Your connected accounts are fine; we just couldn't reach them right now.
                  </p>
                </div>
                {onRetryConnections && (
                  <Button variant="outline" onClick={onRetryConnections}>
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Try again
                  </Button>
                )}
              </>
            ) : (
              <>
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
                <p className="text-sm text-muted-foreground">Loading your channels…</p>
              </>
            )}
          </div>
        )}

        {channels.length === 0 && !channelsUnknown && (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-5 bg-muted/40 p-8 text-center">
            <div className="space-y-1.5">
              <h3 className="text-lg font-semibold tracking-tight">Connect a channel to publish</h3>
              <p className="mx-auto max-w-sm text-sm text-muted-foreground">
                Your post is saved, and you'll come right back here after connecting.
              </p>
            </div>
            <div className="grid w-full max-w-lg grid-cols-2 gap-2 sm:grid-cols-3">{connectButtons("large")}</div>
            <p className="flex max-w-sm items-start gap-1.5 text-left text-xs text-muted-foreground">
              <ShieldCheck className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              Publish through official, secure connections; nothing goes live without your confirmation.
            </p>
          </div>
        )}

        {/* Body: this channel's settings, and how it will look */}
        <div
          className={cn(
            "min-h-0 flex-1 flex-col overflow-y-auto md:flex-row md:overflow-hidden",
            channels.length === 0 ? "hidden" : "flex"
          )}
        >
          <aside className="order-2 shrink-0 p-4 md:order-1 md:min-h-0 md:w-96 md:overflow-y-auto md:border-r md:p-5">
            {focus && (
              <ChannelPanel
                connection={focus}
                isOn={selectedConnectionIds.has(focus.id)}
                offLabel={
                  focus.id in publishedAtById
                    ? publishedLabel(focus)
                    : getDisabledReason(focus) ?? "Not publishing"
                }
                settings={platformSettings[focus.id]}
                onSettingsChange={(patch) => onSettingsChange(focus.id, patch)}
                mediaItems={mediaItems}
                isVideoPost={isVideoPost}
                postTitle={postTitle}
                postDescription={postDescription}
                postFirstComment={postFirstComment}
                defaultPinterestLink={defaultPinterestLink}
                isDescriptionGenerating={pinterestDescriptionGeneratingIds?.has(focus.id)}
                leaveTo={leaveTo}
              />
            )}
          </aside>
          <section className="relative order-1 h-[58dvh] shrink-0 overflow-hidden border-b bg-muted/40 md:order-2 md:h-auto md:min-h-0 md:flex-1 md:shrink md:border-b-0">
            {focus && (
              <div key={focus.id} className={cn("h-full", !selectedConnectionIds.has(focus.id) && "pointer-events-none opacity-35 grayscale")}>
                {renderPreview(focus)}
              </div>
            )}
            {focus && !selectedConnectionIds.has(focus.id) && (
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2.5">
                <p className="rounded-md border bg-background px-3 py-1.5 text-sm font-medium">
                  {focus.id in publishedAtById
                    ? `Published to ${getPlatformConfig(focus.platform)?.name}${
                        publishedAtById[focus.id] ? ` on ${formatDay(publishedAtById[focus.id])}` : ""
                      }`
                    : getDisabledReason(focus) ??
                      `Not publishing to ${getPlatformConfig(focus.platform)?.name}`}
                </p>
                {!getDisabledReason(focus) && (
                  <Button variant="outline" size="sm" onClick={() => onToggleConnection(focus.id, true)}>
                    Add back
                  </Button>
                )}
              </div>
            )}
          </section>
        </div>

        {/* Schedule tray: slides up from the footer, inside the modal. With no
            channels yet the connect panel above is the whole body. */}
        <div
          className={cn(
            "grid shrink-0 transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none",
            showScheduleTray ? "grid-rows-[1fr] border-t" : "grid-rows-[0fr]"
          )}
        >
          <div className={cn("min-h-0", showScheduleTray ? "overflow-visible" : "overflow-hidden")}>
            {showScheduleTray && (
              <div className="px-5 pb-2 pt-4">
                <div className="mb-2.5 flex flex-wrap items-baseline gap-x-2.5">
                  <span className="text-sm font-semibold">Schedule</span>
                  <span className="text-xs text-muted-foreground">
                    Each channel prefills with its next open slot. {timezoneLabel}.
                  </span>
                  {isSuggesting && <Loader2 className="h-3.5 w-3.5 animate-spin self-center text-muted-foreground" />}
                </div>
                <div className="grid grid-cols-1 gap-x-6 gap-y-1 lg:grid-cols-2 xl:grid-cols-3">
                  {selected.map((connection) => {
                    const time = times[connection.id];
                    const slot = suggested[connection.id] ?? existingSchedules[connection.id];
                    return (
                      <div key={connection.id} className="flex flex-wrap items-center gap-2.5 py-1.5 sm:flex-nowrap">
                        <PlatformConnectionIcon
                          platform={connection.platform}
                          profileImage={connection.profileImage}
                          profileName={connection.profileName}
                          size="md"
                          disableTooltip
                        />
                        <div className="flex min-w-0 flex-1 flex-col leading-tight">
                          <span className="truncate text-[13px] font-medium">{connection.profileName}</span>
                          <span className="text-[11.5px] text-muted-foreground">
                            {getPlatformConfig(connection.platform)?.name}
                          </span>
                          {time && slot && time !== slot && (
                            <button
                              type="button"
                              onClick={() => setTimes((prev) => ({ ...prev, [connection.id]: slot }))}
                              className="self-start text-[11.5px] text-muted-foreground underline decoration-border underline-offset-2 hover:text-foreground"
                            >
                              Use next slot
                            </button>
                          )}
                        </div>
                        {time ? (
                          <SchedulePicker
                            value={time}
                            onChange={(value) => setTimes((prev) => ({ ...prev, [connection.id]: value }))}
                            byDate={scheduleByDate}
                            connectionsById={connectionsById}
                            label={`${getPlatformConfig(connection.platform)?.name} (${connection.profileName})`}
                            className="w-full shrink-0 sm:w-auto"
                          />
                        ) : (
                          <div className="h-10 w-full shrink-0 animate-pulse rounded-md bg-muted sm:w-[16.5rem]" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer: both actions apply to every ticked channel */}
        <div className="flex min-h-16 shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-t bg-background px-4 py-3 sm:pl-5">
          {status}
          <div className="ml-auto flex items-center gap-2">
            {isScheduling ? (
              <>
                <Button
                  variant="ghost"
                  // Rescheduling opens straight into this panel; backing out
                  // should leave the schedule alone, not reveal "Publish all".
                  onClick={() => (isReschedule ? onOpenChange(false) : setIsScheduling(false))}
                  disabled={isPublishing}
                >
                  Cancel
                </Button>
                <Button
                  className="bg-[#FF5C35] text-white hover:bg-[#E5502F]"
                  onClick={handleScheduleAll}
                  disabled={isPublishing || isSuggesting || channels.length === 0 || !!blockingMessage}
                >
                  {isPublishing && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                  {isReschedule ? "Update schedule" : count <= 1 ? "Schedule" : "Schedule all"}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  onClick={() => setIsScheduling(true)}
                  disabled={isPublishing || channels.length === 0 || isPublishedEverywhere}
                >
                  <CalendarDays className="mr-1.5 h-4 w-4" />
                  Schedule
                </Button>
                <Button
                  className="bg-[#FF5C35] text-white hover:bg-[#E5502F]"
                  onClick={handlePublishNow}
                  disabled={
                    isPublishing || channels.length === 0 || isPublishedEverywhere || !!blockingMessage
                  }
                >
                  {isPublishing && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                  {count === 1 ? "Publish" : "Publish all"}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ChannelPanel({
  connection,
  isOn,
  offLabel,
  settings,
  onSettingsChange,
  mediaItems,
  isVideoPost,
  postTitle,
  postDescription,
  postFirstComment,
  defaultPinterestLink,
  isDescriptionGenerating,
  leaveTo,
}: {
  connection: PlatformConnection;
  isOn: boolean;
  /** Shown in place of the settings while the channel is off. */
  offLabel: string;
  settings: ConnectionSettings | undefined;
  onSettingsChange: (patch: Partial<ConnectionSettings>) => void;
  mediaItems: MediaItem[];
  isVideoPost: boolean;
  postTitle: string;
  postDescription: string;
  postFirstComment: string;
  defaultPinterestLink?: string;
  isDescriptionGenerating?: boolean;
  leaveTo: (go: () => void) => void;
}) {
  const config = getPlatformConfig(connection.platform);
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2.5 border-b pb-4">
        <PlatformConnectionIcon
          platform={connection.platform}
          profileImage={connection.profileImage}
          profileName={connection.profileName}
          size="md"
          disableTooltip
        />
        <div className="min-w-0 leading-tight">
          <div className="text-xs text-muted-foreground">Settings for</div>
          <div className="truncate text-sm font-semibold">
            {connection.profileName} <span className="font-normal text-muted-foreground">{config?.name}</span>
          </div>
        </div>
        {!isOn && (
          <span className="ml-auto shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            {offLabel}
          </span>
        )}
      </div>
      <div className={cn("space-y-5", !isOn && "pointer-events-none opacity-50")}>
        {connection.publishDefaults && (
          <p className="-mt-1 flex items-start gap-1.5 text-xs text-muted-foreground">
            <History className="mt-px h-3.5 w-3.5 shrink-0" />
            Settings are prefilled from your last post on this channel.
          </p>
        )}
        {settings && (
          <PlatformConnectionSettings
            connection={connection}
            mediaItems={mediaItems}
            settings={settings}
            onSettingsChange={onSettingsChange}
            postDescription={postDescription}
            postTitle={postTitle}
            defaultPinterestLink={defaultPinterestLink}
            isDescriptionGenerating={isDescriptionGenerating}
            isVideoPost={isVideoPost}
          />
        )}
        <FirstCommentField
          platform={connection.platform}
          settings={settings as FirstCommentOverrides | undefined}
          onChange={(patch) => onSettingsChange(patch as Partial<ConnectionSettings>)}
          postFirstComment={postFirstComment}
          leaveTo={leaveTo}
        />
      </div>
    </div>
  );
}

function FirstCommentField({
  platform,
  settings,
  onChange,
  postFirstComment,
  leaveTo,
}: {
  platform: PlatformType;
  settings: FirstCommentOverrides | undefined;
  onChange: (patch: FirstCommentOverrides) => void;
  postFirstComment: string;
  leaveTo: (go: () => void) => void;
}) {
  const { isStarterOrAbove } = useStripe();
  const { navigateToUpgrade } = useUpgradePage();
  const name = getPlatformConfig(platform)?.name;

  if (!FIRST_COMMENT_PLATFORMS.has(platform)) {
    return (
      <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
        <Info className="mt-px h-3.5 w-3.5 shrink-0" />
        First comments aren't supported on {name}, so it won't post there.
      </p>
    );
  }

  const enabled = !!settings?.firstCommentEnabled && isStarterOrAbove;
  const override = settings?.firstComment?.trim() ? settings.firstComment : undefined;
  const isInherited = override === undefined;
  const shown = override ?? postFirstComment;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5">
            <Label>First comment</Label>
            {!isStarterOrAbove && (
              <span className="inline-flex items-center rounded-full bg-[#FF5C35]/10 p-1 text-[#FF5C35]">
                <Crown className="h-2.5 w-2.5" />
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {isStarterOrAbove ? (
              "Auto-post a comment after publishing"
            ) : (
              <button
                type="button"
                onClick={() => leaveTo(() => void navigateToUpgrade({ reason: "first_comment" }))}
                className="text-[#FF5C35] hover:underline"
              >
                Upgrade to auto-post a comment after publishing
              </button>
            )}
          </p>
        </div>
        <Switch
          checked={enabled}
          disabled={!isStarterOrAbove}
          onCheckedChange={(checked) => onChange({ firstCommentEnabled: checked })}
          aria-label={`First comment on ${name}`}
        />
      </div>
      {enabled && (
        <div className="space-y-2">
          <div className="flex min-h-7 items-center justify-between">
            <Label htmlFor={`first-comment-${platform}`} className="text-xs text-muted-foreground">
              Comment
            </Label>
            {!isInherited && postFirstComment && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-muted-foreground"
                onClick={() => onChange({ firstComment: undefined })}
              >
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                Use post comment
              </Button>
            )}
          </div>
          <Textarea
            id={`first-comment-${platform}`}
            value={shown}
            onChange={(e) => {
              const next = e.target.value;
              onChange({ firstComment: next === "" || next === postFirstComment ? undefined : next });
            }}
            placeholder="e.g. your website URL, hashtags, or a call to action..."
            className={cn("min-h-[80px] resize-y", isInherited && postFirstComment && INHERITED_FIELD)}
          />
          {isInherited && postFirstComment && (
            <p className="text-xs italic text-muted-foreground">Same as post. Type to override.</p>
          )}
        </div>
      )}
    </div>
  );
}

function nextHour(): number {
  const next = new Date();
  next.setHours(next.getHours() + 1, 0, 0, 0);
  return next.getTime();
}

/** "Today 6:00 PM", "Thu 8:30 AM", "Oct 12 9:00 AM". */
/** "Oct 5", or "Oct 5, 2025" outside this year. */
function formatDay(time: number): string {
  const date = new Date(time);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() !== new Date().getFullYear() && { year: "numeric" }),
  });
}

function formatShort(time: number): string {
  const date = new Date(time);
  const days = Math.round(
    (new Date(time).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) / 86_400_000
  );
  const clock = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  if (days === 0) return `Today ${clock}`;
  if (days > 0 && days < 7) return `${date.toLocaleDateString("en-US", { weekday: "short" })} ${clock}`;
  return `${date.toLocaleDateString("en-US", { month: "short", day: "numeric" })} ${clock}`;
}

